import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Shield, Car, Home, CreditCard, RefreshCw, AlertCircle, Search, Download, X, BarChart3, PieChart, TrendingUp, Edit2, Save } from 'lucide-react';
import { ChecklistType, CHECKLIST_NAMES, ChecklistRecord } from '../types/datamodel';
import { InfoPrincipaliForm } from './forms/InfoPrincipaliForm';
import { ParteAForm } from './forms/ParteAForm';
import { ParteBForm } from './forms/ParteBForm';
import { ParteCForm } from './forms/ParteCForm';
import { ChecklistTabs, TabType } from './ChecklistTabs';
import * as API from '../services/api';

interface DashboardProps {
  userRole: 'bank' | 'company';
  currentBankId?: string;
  apiBaseUrl: string;
}

interface ChecklistData {
  type: ChecklistType;
  records: any[]; // Using any because API returns snake_case, not camelCase
  loading: boolean;
  error: string | null;
}

const checklistIcons: Record<ChecklistType, React.FC<any>> = {
  ibip: FileText,
  protection: Shield,
  motor: Car,
  pnc: Home,
  ppi: CreditCard
};

export function Dashboard({ userRole, currentBankId, apiBaseUrl }: DashboardProps) {
  const [activeTab, setActiveTab] = useState<ChecklistType>('ibip');
  const [checklistsData, setChecklistsData] = useState<Map<ChecklistType, ChecklistData>>(new Map());
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [outcomeFilter, setOutcomeFilter] = useState<'all' | 'positive' | 'negative'>('all');

  console.log('🎯 Dashboard Received Props:', { userRole, currentBankId });

  const checklistTypes: ChecklistType[] = ['ibip', 'protection', 'motor', 'pnc', 'ppi'];

  // Funzione per caricare i dati di una checklist specifica
  const loadChecklistData = async (type: ChecklistType) => {
    // Se c'è un currentBankId, filtra per banca specifica (sia per utenti bank che company)
    // Altrimenti, mostra tutti i record
    const endpoint = currentBankId
      ? `${apiBaseUrl}/api/checklist/${currentBankId}/${type}`
      : `${apiBaseUrl}/api/checklist/all/${type}`;

    console.log('📊 Dashboard loadChecklistData:', { userRole, currentBankId, type, endpoint });

    setChecklistsData(prev => {
      const updated = new Map(prev);
      updated.set(type, {
        type,
        records: [],
        loading: true,
        error: null
      });
      return updated;
    });

    try {
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      setChecklistsData(prev => {
        const updated = new Map(prev);
        updated.set(type, {
          type,
          records: data.records || [],
          loading: false,
          error: null
        });
        return updated;
      });
    } catch (error) {
      console.error(`Error loading ${type}:`, error);
      setChecklistsData(prev => {
        const updated = new Map(prev);
        updated.set(type, {
          type,
          records: [],
          loading: false,
          error: error instanceof Error ? error.message : 'Errore nel caricamento dei dati'
        });
        return updated;
      });
    }
  };

  // Carica i dati al mount e quando cambia la banca
  useEffect(() => {
    checklistTypes.forEach(type => {
      loadChecklistData(type);
    });
  }, [currentBankId, userRole]);

  // Funzione per ricaricare tutti i dati
  const handleRefreshAll = async () => {
    setRefreshing(true);
    await Promise.all(checklistTypes.map(type => loadChecklistData(type)));
    setRefreshing(false);
  };

  // Ottieni i dati della checklist attiva
  const activeChecklistData = checklistsData.get(activeTab) || {
    type: activeTab,
    records: [],
    loading: false,
    error: null
  };

  // Funzione per verificare se un record è completo (tutti i campi mandatory compilati)
  const isRecordCompleted = (record: any): boolean => {
    // Campi mandatory delle info principali
    const hasInfoPrincipali =
      record.numero_polizza &&
      record.compagnia &&
      record.nominativo_contraente &&
      record.codice_fiscale_contraente;

    // Verifica che esistano le parti A, B, C
    const hasParteA = record.parte_a && Object.keys(record.parte_a).length > 0;
    const hasParteB = record.parte_b && Object.keys(record.parte_b).length > 0;
    const hasParteC = record.parte_c && Object.keys(record.parte_c).length > 0;

    // Verifica che ogni sezione abbia almeno un campo "esito" compilato
    let hasEsiti = false;
    if (hasParteA) {
      hasEsiti = Object.values(record.parte_a).some((section: any) =>
        section && typeof section === 'object' && section.esito && section.esito !== ''
      );
    }

    return hasInfoPrincipali && hasParteA && hasParteB && hasParteC && hasEsiti;
  };

  // Funzione per verificare se un record ha esito positivo
  const hasPositiveOutcome = (record: any): boolean => {
    if (!record.parte_a && !record.parte_b && !record.parte_c) return false;

    const allEsiti: string[] = [];

    // Raccoglie SOLO i campi "esito" dalle parti A, B, C
    [record.parte_a, record.parte_b, record.parte_c].forEach(parte => {
      if (parte && typeof parte === 'object') {
        Object.values(parte).forEach((section: any) => {
          if (section && typeof section === 'object' && section.esito) {
            allEsiti.push(String(section.esito).toUpperCase());
          }
        });
      }
    });

    // Se non ci sono esiti, non può essere positivo
    if (allEsiti.length === 0) return false;

    // Tutti gli esiti devono essere "CONFORME"
    return allEsiti.every(esito => esito === 'CONFORME');
  };

  // Funzione per verificare se un record ha esito negativo
  const hasNegativeOutcome = (record: any): boolean => {
    if (!record.parte_a && !record.parte_b && !record.parte_c) return false;

    const allEsiti: string[] = [];

    // Raccoglie SOLO i campi "esito" dalle parti A, B, C
    [record.parte_a, record.parte_b, record.parte_c].forEach(parte => {
      if (parte && typeof parte === 'object') {
        Object.values(parte).forEach((section: any) => {
          if (section && typeof section === 'object' && section.esito) {
            allEsiti.push(String(section.esito).toUpperCase());
          }
        });
      }
    });

    // Se non ci sono esiti, non può essere negativo
    if (allEsiti.length === 0) return false;

    // Almeno un esito deve essere "NON CONFORME"
    return allEsiti.some(esito => esito === 'NON CONFORME');
  };

  // Filtra i record in base alla ricerca e all'esito
  const filteredRecords = useMemo(() => {
    let records = activeChecklistData.records;

    // Filtra per esito
    if (outcomeFilter === 'positive') {
      records = records.filter(record => hasPositiveOutcome(record));
    } else if (outcomeFilter === 'negative') {
      records = records.filter(record => hasNegativeOutcome(record));
    }

    // Filtra per query di ricerca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      records = records.filter(record =>
        (record.numero_polizza?.toLowerCase().includes(query)) ||
        (record.nominativo_contraente?.toLowerCase().includes(query)) ||
        (record.codice_fiscale_contraente?.toLowerCase().includes(query))
      );
    }

    return records;
  }, [activeChecklistData.records, searchQuery, outcomeFilter]);

  // Calcola statistiche aggregate
  const statistics = useMemo(() => {
    const allRecords: any[] = [];
    checklistsData.forEach(data => {
      allRecords.push(...data.records);
    });

    const totalRecords = allRecords.length;

    // Nuova logica per gli stati
    const completedRecords = allRecords.filter(r => isRecordCompleted(r));
    const draftRecords = allRecords.filter(r => !isRecordCompleted(r));
    const positiveRecords = allRecords.filter(r => hasPositiveOutcome(r));
    const negativeRecords = allRecords.filter(r => hasNegativeOutcome(r));

    const byStatus = {
      draft: draftRecords.length,
      completed: completedRecords.length,
      positive: positiveRecords.length,
      negative: negativeRecords.length,
    };

    const byCompany: Record<string, number> = {};
    allRecords.forEach(record => {
      const company = record.compagnia || 'Sconosciuta';
      byCompany[company] = (byCompany[company] || 0) + 1;
    });

    const byBank: Record<string, number> = {};
    allRecords.forEach(record => {
      const bank = record.bank_name || 'Sconosciuta';
      byBank[bank] = (byBank[bank] || 0) + 1;
    });

    const byChecklistType: Record<string, number> = {};
    checklistTypes.forEach(type => {
      const data = checklistsData.get(type);
      byChecklistType[CHECKLIST_NAMES[type]] = data?.records.length || 0;
    });

    return {
      totalRecords,
      byStatus,
      byCompany,
      byBank,
      byChecklistType,
    };
  }, [checklistsData, checklistTypes]);

  // Funzione per esportare in CSV
  const handleExportCSV = () => {
    if (filteredRecords.length === 0) return;

    const headers = ['Banca', 'Codice Banca', 'N° Polizza', 'Contraente', 'Codice Fiscale', 'Compagnia', 'Data Decorrenza', 'Data Scadenza', 'Stato', 'Ultimo Agg.'];
    const csvRows = [headers.join(',')];

    filteredRecords.forEach(record => {
      const row = [
        record.bank_name || '',
        record.bank_code || '',
        record.numero_polizza || '',
        record.nominativo_contraente || '',
        record.codice_fiscale_contraente || '',
        record.compagnia || '',
        record.data_decorrenza ? new Date(record.data_decorrenza).toLocaleDateString('it-IT') : '',
        record.data_scadenza ? new Date(record.data_scadenza).toLocaleDateString('it-IT') : '',
        record.status === 'draft' ? 'Bozza' : record.status === 'completed' ? 'Completato' : 'Revisionato',
        record.updated_at ? new Date(record.updated_at).toLocaleDateString('it-IT') : ''
      ].map(field => `"${field}"`);
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `checklist_${CHECKLIST_NAMES[activeTab]}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Dashboard Checklist</h1>
            <p className="text-gray-600 mt-1">Visualizzazione controlli per tutte le tipologie</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowStats(!showStats)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                showStats
                  ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-600'
                  : 'bg-white text-indigo-600 border-2 border-indigo-600 hover:bg-indigo-50'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
              <span>Statistiche</span>
            </button>
            <button
              onClick={handleRefreshAll}
              disabled={refreshing}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                refreshing
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Aggiornamento...' : 'Aggiorna'}</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6">
          {checklistTypes.map(type => {
            const data = checklistsData.get(type);
            const Icon = checklistIcons[type];
            const recordCount = data?.records?.length || 0;

            return (
              <div
                key={type}
                className="bg-gradient-to-br from-indigo-50 to-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-6 h-6 text-indigo-600" />
                  {data?.loading && (
                    <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
                  )}
                </div>
                <p className="text-2xl font-bold text-gray-800">{recordCount}</p>
                <p className="text-sm text-gray-600">{CHECKLIST_NAMES[type]}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabs - Only show when not in stats view */}
      {!showStats && (
        <div className="border-b border-gray-200 bg-white">
          <div className="flex space-x-1 px-8 overflow-x-auto">
            {checklistTypes.map(type => {
              const Icon = checklistIcons[type];
              const isActive = activeTab === type;

              return (
                <button
                  key={type}
                  onClick={() => setActiveTab(type)}
                  className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{CHECKLIST_NAMES[type]}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search and Export Bar - Only show when not in stats view */}
      {!showStats && (
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          {/* Outcome Filter Buttons */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-medium text-gray-700 mr-2">Filtra per esito:</span>
            <button
              onClick={() => setOutcomeFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                outcomeFilter === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tutti ({activeChecklistData.records.length})
            </button>
            <button
              onClick={() => setOutcomeFilter('positive')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                outcomeFilter === 'positive'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Esito Positivo ({activeChecklistData.records.filter(r => hasPositiveOutcome(r)).length})
            </button>
            <button
              onClick={() => setOutcomeFilter('negative')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                outcomeFilter === 'negative'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Esito Negativo ({activeChecklistData.records.filter(r => hasNegativeOutcome(r)).length})
            </button>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca per polizza, contraente o codice fiscale..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {searchQuery && (
                <span className="text-sm text-gray-600">
                  {filteredRecords.length} di {activeChecklistData.records.length} record
                </span>
              )}
              <button
                onClick={handleExportCSV}
                disabled={filteredRecords.length === 0}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  filteredRecords.length === 0
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                <Download className="w-5 h-5" />
                <span>Esporta CSV</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8">
        {showStats ? (
          <StatisticsView statistics={statistics} />
        ) : activeChecklistData.loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Caricamento dati...</p>
            </div>
          </div>
        ) : activeChecklistData.error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-800 mb-2">Errore nel caricamento</h3>
                <p className="text-red-700">{activeChecklistData.error}</p>
                <button
                  onClick={() => loadChecklistData(activeTab)}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Riprova
                </button>
              </div>
            </div>
          </div>
        ) : activeChecklistData.records.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <FileText className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Nessun controllo presente</h3>
            <p className="text-yellow-700">
              Non ci sono controlli registrati per la checklist {CHECKLIST_NAMES[activeTab]}.
            </p>
            <p className="text-yellow-600 text-sm mt-2">
              Utilizza il menu laterale per accedere al form di compilazione.
            </p>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
            <Search className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Nessun risultato trovato</h3>
            <p className="text-blue-700">
              {searchQuery && outcomeFilter !== 'all'
                ? `La ricerca per "${searchQuery}" con filtro "${outcomeFilter === 'positive' ? 'Esito Positivo' : 'Esito Negativo'}" non ha prodotto risultati.`
                : searchQuery
                ? `La ricerca per "${searchQuery}" non ha prodotto risultati.`
                : outcomeFilter === 'positive'
                ? 'Nessun record con esito positivo trovato.'
                : 'Nessun record con esito negativo trovato.'
              }
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Cancella ricerca
                </button>
              )}
              {outcomeFilter !== 'all' && (
                <button
                  onClick={() => setOutcomeFilter('all')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Mostra tutti
                </button>
              )}
            </div>
          </div>
        ) : (
          <ChecklistTable
            records={filteredRecords}
            type={activeTab}
            onRowClick={setSelectedRecord}
          />
        )}
      </div>

      {/* Detail Modal */}
      {selectedRecord && (
        <RecordDetailModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
}

// Componente per la tabella dei record
function ChecklistTable({ records, type, onRowClick }: { records: any[]; type: ChecklistType; onRowClick: (record: any) => void }) {
  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Banca
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                N° Polizza
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contraente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Codice Fiscale
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Compagnia
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data Decorrenza
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data Scadenza
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stato
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ultimo Agg.
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {records.map((record, index) => (
              <tr
                key={record.id || index}
                onClick={() => onRowClick(record)}
                className="hover:bg-indigo-50 transition-colors cursor-pointer">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {record.bank_name || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">{record.bank_code || ''}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {record.numero_polizza || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{record.nominativo_contraente || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 font-mono">
                    {record.codice_fiscale_contraente || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{record.compagnia || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {record.data_decorrenza
                      ? new Date(record.data_decorrenza).toLocaleDateString('it-IT')
                      : '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {record.data_scadenza
                      ? new Date(record.data_scadenza).toLocaleDateString('it-IT')
                      : '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      record.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : record.status === 'reviewed'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {record.status === 'draft' && 'Bozza'}
                    {record.status === 'completed' && 'Completato'}
                    {record.status === 'reviewed' && 'Revisionato'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {record.updated_at
                    ? new Date(record.updated_at).toLocaleDateString('it-IT', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer con conteggio */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <p className="text-sm text-gray-600">
          Totale record: <span className="font-semibold">{records.length}</span>
        </p>
      </div>
    </div>
  );
}

// Componente Modal per i dettagli del record
function RecordDetailModal({ record, onClose }: { record: any; onClose: () => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Convert API response format to form format
  const convertToFormData = (apiRecord: any) => {
    // Helper to convert date strings to Date objects recursively
    const convertDates = (obj: any): any => {
      if (!obj || typeof obj !== 'object') return obj;

      const result: any = Array.isArray(obj) ? [] : {};

      for (const [key, value] of Object.entries(obj)) {
        // Convert date fields (dataIncasso, dataSottoscrizione, etc.)
        if (key.toLowerCase().includes('data') && typeof value === 'string' && value) {
          try {
            result[key] = new Date(value);
          } catch {
            result[key] = value;
          }
        } else if (value && typeof value === 'object') {
          // Recursively convert nested objects
          result[key] = convertDates(value);
        } else {
          result[key] = value;
        }
      }

      return result;
    };

    // Process infoPrincipali: convert dates whether they come from info_principali or direct fields
    let infoPrincipali;
    if (apiRecord.info_principali) {
      // If info_principali exists, merge with direct fields and convert dates
      infoPrincipali = {
        numeroPolizza: apiRecord.info_principali.numeroPolizza || apiRecord.numero_polizza || '',
        compagnia: apiRecord.info_principali.compagnia || apiRecord.compagnia || '',
        tipologiaProdotto: apiRecord.info_principali.tipologiaProdotto || '',
        nomeCommerciale: apiRecord.info_principali.nomeCommerciale || '',
        codiceFiscaleContraente: apiRecord.info_principali.codiceFiscaleContraente || apiRecord.codice_fiscale_contraente || '',
        nominativoContraente: apiRecord.info_principali.nominativoContraente || apiRecord.nominativo_contraente || '',
        tipologiaOperazione: apiRecord.info_principali.tipologiaOperazione || '',
        dataSottoscrizione: apiRecord.info_principali.dataSottoscrizione
          ? new Date(apiRecord.info_principali.dataSottoscrizione)
          : (apiRecord.data_decorrenza ? new Date(apiRecord.data_decorrenza) : null),
        dataOperazione: apiRecord.info_principali.dataOperazione
          ? new Date(apiRecord.info_principali.dataOperazione)
          : (apiRecord.data_scadenza ? new Date(apiRecord.data_scadenza) : null),
        importo: apiRecord.info_principali.importo || null,
        dataControllo: apiRecord.info_principali.dataControllo ? new Date(apiRecord.info_principali.dataControllo) : null,
        controlloEffettuato: apiRecord.info_principali.controlloEffettuato || '',
        referente: apiRecord.info_principali.referente || ''
      };
    } else {
      // Build from direct fields with all required InfoPrincipali fields
      infoPrincipali = {
        numeroPolizza: apiRecord.numero_polizza || '',
        compagnia: apiRecord.compagnia || '',
        tipologiaProdotto: '',
        nomeCommerciale: '',
        codiceFiscaleContraente: apiRecord.codice_fiscale_contraente || '',
        nominativoContraente: apiRecord.nominativo_contraente || '',
        tipologiaOperazione: '',
        dataSottoscrizione: apiRecord.data_decorrenza ? new Date(apiRecord.data_decorrenza) : null,
        dataOperazione: apiRecord.data_scadenza ? new Date(apiRecord.data_scadenza) : null,
        importo: null,
        dataControllo: null,
        controlloEffettuato: '',
        referente: ''
      };
    }

    // Default structures for parteA, parteB, parteC
    const defaultParteA = {
      a1: { dataIncasso: null, correttoAddebito: '', esito: '', note: '' },
      a2: { attestazioneConsegnaMUP: '', esito: '', note: '' },
      a3: { presenzaPropostaContratto: '', presenzaFirmaProposta: '', esito: '', note: '' },
      a4: { presenzaQuestionario: '', presenzaFirmaQuestionario: '', esito: '', note: '' },
      a5: { materialePersonalizzato: '', esito: '', note: '' },
      a6: { aggiornamentoSetInformativo: '', presenzaSetSito: '', esito: '', note: '' }
    };

    const defaultParteB = {
      b1: { documentiIdentificazione: '', esito: '', note: '' },
      b2: { qAdv: '', presenzaFirmaQAdv: '', esito: '', note: '' }
    };

    const defaultParteC = {
      c1: { presenzaFirmaPrivacy: '', esito: '', note: '' },
      c2: { formazioneObbligatoria: '', esito: '', note: '' },
      c3: { collocatoreAbilitato: '', verificaIscrizioneRUI: '', esito: '', note: '' },
      c4: { presenzaModuloFATCA: '', presenzaFirmaFATCA: '', esito: '', note: '' }
    };

    // Merge API data with defaults to ensure all fields exist
    const mergeWithDefaults = (apiData: any, defaults: any) => {
      if (!apiData) return { ...defaults };
      const result: any = {};
      // First, include all keys from defaults
      for (const key of Object.keys(defaults)) {
        const apiSection = apiData[key];
        const defaultSection = defaults[key];
        // Always merge default with API data (API data takes precedence)
        result[key] = { ...defaultSection, ...(apiSection || {}) };
      }
      // Also include any extra keys from API data not in defaults
      for (const key of Object.keys(apiData)) {
        if (!result[key]) {
          result[key] = apiData[key];
        }
      }
      return result;
    };

    return {
      infoPrincipali,
      parteA: mergeWithDefaults(convertDates(apiRecord.parte_a), defaultParteA),
      parteB: mergeWithDefaults(convertDates(apiRecord.parte_b), defaultParteB),
      parteC: mergeWithDefaults(convertDates(apiRecord.parte_c), defaultParteC)
    };
  };

  const [formData, setFormData] = useState(() => {
    const converted = convertToFormData(record);
    console.log('🔍 Initial form data conversion:', {
      original_record: record,
      converted_formData: converted,
      parteA_sample: converted.parteA?.a1,
      parteB_sample: converted.parteB?.b1,
      parteC_sample: converted.parteC?.c1
    });
    return converted;
  });

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const recordData = {
        bank_id: record.bank_id,
        created_by: record.created_by,
        checklist_type: record.checklist_type,
        numero_polizza: formData.infoPrincipali.numeroPolizza,
        compagnia: formData.infoPrincipali.compagnia || '',
        data_decorrenza: formData.infoPrincipali.dataSottoscrizione?.toISOString() || new Date().toISOString(),
        data_scadenza: formData.infoPrincipali.dataOperazione?.toISOString() || new Date().toISOString(),
        nominativo_contraente: formData.infoPrincipali.nominativoContraente,
        codice_fiscale_contraente: formData.infoPrincipali.codiceFiscaleContraente
      };

      await API.saveChecklistRecord(
        recordData,
        formData.parteA,
        formData.parteB,
        formData.parteC,
        formData.infoPrincipali
      );

      alert('✅ Modifiche salvate con successo!');
      setIsEditing(false);
      // Reload the page to show updated data
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante il salvataggio');
      alert('❌ Errore: ' + (err instanceof Error ? err.message : 'Unknown'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">{isEditing ? 'Modifica Controllo' : 'Dettagli Controllo'}</h2>
            <p className="text-indigo-100 text-sm mt-1">Polizza: {record.numero_polizza}</p>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
              >
                <Edit2 className="w-4 h-4" />
                Modifica
              </button>
            )}
            <button
              onClick={onClose}
              className="text-white hover:bg-indigo-700 p-2 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs - only shown in edit mode */}
        {isEditing && (
          <ChecklistTabs activeTab={activeTab} onTabChange={setActiveTab} />
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isEditing ? (
            // Edit mode: show forms
            <>
              {activeTab === 'info' && (
                <InfoPrincipaliForm
                  data={formData.infoPrincipali}
                  checklistType={record.checklist_type as ChecklistType}
                  onChange={(data) => setFormData(prev => ({ ...prev, infoPrincipali: data }))}
                />
              )}
              {activeTab === 'parteA' && (
                <ParteAForm
                  data={formData.parteA}
                  infoPrincipali={formData.infoPrincipali}
                  checklistType={record.checklist_type as ChecklistType}
                  onChange={(data) => setFormData(prev => ({ ...prev, parteA: data }))}
                />
              )}
              {activeTab === 'parteB' && (
                <ParteBForm
                  data={formData.parteB}
                  onChange={(data) => setFormData(prev => ({ ...prev, parteB: data }))}
                />
              )}
              {activeTab === 'parteC' && (
                <ParteCForm
                  data={formData.parteC}
                  checklistType={record.checklist_type as ChecklistType}
                  onChange={(data) => setFormData(prev => ({ ...prev, parteC: data }))}
                />
              )}
            </>
          ) : (
            // View mode: show read-only data
            <>
              {/* Informazioni Generali */}
              <section className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                  Informazioni Generali
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DetailField label="Banca" value={record.bank_name} />
                  <DetailField label="Codice Banca" value={record.bank_code} />
                  <DetailField label="Numero Polizza" value={record.numero_polizza} />
                  <DetailField label="Compagnia" value={record.compagnia} />
                  <DetailField label="Nominativo Contraente" value={record.nominativo_contraente} />
                  <DetailField label="Codice Fiscale" value={record.codice_fiscale_contraente} />
                  <DetailField
                    label="Data Decorrenza"
                    value={record.data_decorrenza ? new Date(record.data_decorrenza).toLocaleDateString('it-IT') : '-'}
                  />
                  <DetailField
                    label="Data Scadenza"
                    value={record.data_scadenza ? new Date(record.data_scadenza).toLocaleDateString('it-IT') : '-'}
                  />
                  <DetailField
                    label="Stato"
                    value={
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          record.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : record.status === 'reviewed'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {record.status === 'draft' && 'Bozza'}
                        {record.status === 'completed' && 'Completato'}
                        {record.status === 'reviewed' && 'Revisionato'}
                      </span>
                    }
                  />
                  <DetailField
                    label="Ultimo Aggiornamento"
                    value={record.updated_at ? new Date(record.updated_at).toLocaleString('it-IT') : '-'}
                  />
                </div>
              </section>

          {/* Parte A */}
          {record.parte_a && (
            <section className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                Parte A - Controlli Generali
              </h3>
              <div className="space-y-4">
                {Object.entries(record.parte_a).map(([key, value]) => {
                  if (key === 'id' || key === 'checklist_record_id') return null;

                  // Se il valore è un oggetto con sotto-campi, mostralo espanso
                  if (value && typeof value === 'object' && !Array.isArray(value)) {
                    return (
                      <div key={key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="font-semibold text-gray-800 mb-3 uppercase text-sm">
                          {key}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(value as any).map(([subKey, subValue]) => (
                            <div key={subKey} className="flex flex-col">
                              <span className="text-xs text-gray-500 mb-1">
                                {subKey.replace(/([A-Z])/g, ' $1').trim().replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {subValue === null || subValue === '' ? '-' : String(subValue)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <span className="text-sm font-medium text-gray-700">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <CheckIcon value={value} />
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Parte B */}
          {record.parte_b && (
            <section className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                Parte B - Controlli Specifici
              </h3>
              <div className="space-y-4">
                {Object.entries(record.parte_b).map(([key, value]) => {
                  if (key === 'id' || key === 'checklist_record_id') return null;

                  // Se il valore è un oggetto con sotto-campi, mostralo espanso
                  if (value && typeof value === 'object' && !Array.isArray(value)) {
                    return (
                      <div key={key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="font-semibold text-gray-800 mb-3 uppercase text-sm">
                          {key}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(value as any).map(([subKey, subValue]) => (
                            <div key={subKey} className="flex flex-col">
                              <span className="text-xs text-gray-500 mb-1">
                                {subKey.replace(/([A-Z])/g, ' $1').trim().replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {subValue === null || subValue === '' ? '-' : String(subValue)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <span className="text-sm font-medium text-gray-700">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <CheckIcon value={value} />
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Parte C */}
          {record.parte_c && (
            <section className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                Parte C - Controlli Avanzati
              </h3>
              <div className="space-y-4">
                {Object.entries(record.parte_c).map(([key, value]) => {
                  if (key === 'id' || key === 'checklist_record_id') return null;

                  // Se il valore è un oggetto con sotto-campi, mostralo espanso
                  if (value && typeof value === 'object' && !Array.isArray(value)) {
                    return (
                      <div key={key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="font-semibold text-gray-800 mb-3 uppercase text-sm">
                          {key}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(value as any).map(([subKey, subValue]) => (
                            <div key={subKey} className="flex flex-col">
                              <span className="text-xs text-gray-500 mb-1">
                                {subKey.replace(/([A-Z])/g, ' $1').trim().replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {subValue === null || subValue === '' ? '-' : String(subValue)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <span className="text-sm font-medium text-gray-700">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <CheckIcon value={value} />
                    </div>
                  );
                })}
              </div>
            </section>
          )}

              {!record.parte_a && !record.parte_b && !record.parte_c && (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>Nessun dettaglio di controllo disponibile per questo record.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - only shown in edit mode */}
        {isEditing && (
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex items-center justify-between">
            {error && (
              <div className="flex items-center text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 mr-2" />
                {error}
              </div>
            )}
            <div className="flex items-center gap-3 ml-auto">
              <button
                onClick={() => setIsEditing(false)}
                disabled={saving}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="w-5 h-5" />
                <span>{saving ? 'Salvataggio...' : 'Salva Modifiche'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper component per i campi di dettaglio
function DetailField({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>
      <div className="text-sm text-gray-900 font-medium">
        {typeof value === 'string' || typeof value === 'number' ? value || '-' : value}
      </div>
    </div>
  );
}

// Helper component per icone di check
function CheckIcon({ value }: { value: any }) {
  // Handle objects by rendering them as JSON
  if (value && typeof value === 'object') {
    return (
      <div className="text-xs text-gray-600 bg-gray-100 rounded p-2 max-w-md">
        <pre className="whitespace-pre-wrap break-words">
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    );
  }

  if (value === true || value === 'true' || value === 1) {
    return (
      <span className="flex items-center text-green-600">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      </span>
    );
  } else if (value === false || value === 'false' || value === 0) {
    return (
      <span className="flex items-center text-red-600">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      </span>
    );
  } else {
    return <span className="text-sm text-gray-600">{value || '-'}</span>;
  }
}

// Helper function to get color for checklist type
function getChecklistTypeColor(typeName: string): string {
  const colorMap: Record<string, string> = {
    'IBIP Saving': '#6366f1',      // indigo
    'Protection Vita': '#8b5cf6',  // purple
    'Motor': '#ec4899',            // pink
    'P&C Non Motor': '#3b82f6',    // blue
    'PPI Standard': '#14b8a6'      // teal
  };
  return colorMap[typeName] || '#6366f1';
}

// Donut Chart Component
interface DonutChartData {
  label: string;
  value: number;
  color: string;
}

function DonutChart({ data, total }: { data: DonutChartData[]; total: number }) {
  const size = 240;
  const strokeWidth = 40;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let currentAngle = -90; // Start from top

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />

          {/* Data segments */}
          {data.map((item, index) => {
            const percentage = (item.value / total) * 100;
            const segmentLength = (percentage / 100) * circumference;
            const offset = circumference - segmentLength;
            const rotation = currentAngle;

            // Update angle for next segment
            currentAngle += (percentage / 100) * 360;

            return (
              <circle
                key={index}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={item.color.startsWith('#') ? item.color : `var(--${item.color})`}
                strokeWidth={strokeWidth}
                strokeDasharray={`${segmentLength} ${circumference}`}
                strokeDashoffset={offset}
                className="transition-all duration-500"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transformOrigin: 'center',
                }}
              />
            );
          })}
        </svg>

        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-800">{total}</div>
            <div className="text-sm text-gray-500">Totale</div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 w-full space-y-2">
        {data.map((item, index) => {
          const percentage = ((item.value / total) * 100).toFixed(1);
          return (
            <div key={index} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: item.color.startsWith('#') ? item.color : undefined }}
                />
                <span className="font-medium text-gray-700">{item.label}</span>
              </div>
              <span className="text-gray-600 font-semibold">
                {item.value} ({percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Statistics View Component
function StatisticsView({ statistics }: { statistics: any }) {
  const statusColors = {
    draft: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    completed: 'bg-green-100 text-green-800 border-green-300',
    positive: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    negative: 'bg-red-100 text-red-800 border-red-300',
  };

  const companyColors = ['#6366f1', '#8b5cf6', '#ec4899', '#3b82f6'];
  const bankColors = ['#10b981', '#14b8a6', '#06b6d4', '#0ea5e9'];

  return (
    <div className="space-y-6">
      {/* Overview Section */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-6 h-6 text-indigo-600" />
          <h2 className="text-2xl font-bold text-gray-800">Panoramica Generale</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-8 h-8 opacity-80" />
            </div>
            <p className="text-3xl font-bold mb-1">{statistics.totalRecords}</p>
            <p className="text-indigo-100 text-sm">Totale Controlli</p>
          </div>
          <div className={`${statusColors.draft} border-2 rounded-lg p-6 shadow-lg`}>
            <p className="text-3xl font-bold mb-1">{statistics.byStatus.draft}</p>
            <p className="text-sm font-medium">Bozze</p>
            <div className="mt-2 h-2 bg-yellow-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-yellow-600"
                style={{ width: `${statistics.totalRecords > 0 ? (statistics.byStatus.draft / statistics.totalRecords) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className={`${statusColors.completed} border-2 rounded-lg p-6 shadow-lg`}>
            <p className="text-3xl font-bold mb-1">{statistics.byStatus.completed}</p>
            <p className="text-sm font-medium">Completati</p>
            <div className="mt-2 h-2 bg-green-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-600"
                style={{ width: `${statistics.totalRecords > 0 ? (statistics.byStatus.completed / statistics.totalRecords) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className={`${statusColors.positive} border-2 rounded-lg p-6 shadow-lg`}>
            <p className="text-3xl font-bold mb-1">{statistics.byStatus.positive}</p>
            <p className="text-sm font-medium">Esito Positivo</p>
            <div className="mt-2 h-2 bg-emerald-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-600"
                style={{ width: `${statistics.totalRecords > 0 ? (statistics.byStatus.positive / statistics.totalRecords) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className={`${statusColors.negative} border-2 rounded-lg p-6 shadow-lg`}>
            <p className="text-3xl font-bold mb-1">{statistics.byStatus.negative}</p>
            <p className="text-sm font-medium">Esito Negativo</p>
            <div className="mt-2 h-2 bg-red-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-red-600"
                style={{ width: `${statistics.totalRecords > 0 ? (statistics.byStatus.negative / statistics.totalRecords) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Distribution by Checklist Type */}
      <section className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <PieChart className="w-6 h-6 text-indigo-600" />
          <h3 className="text-xl font-bold text-gray-800">Distribuzione per Tipo Checklist</h3>
        </div>
        <DonutChart
          data={Object.entries(statistics.byChecklistType).map(([type, count]: [string, any]) => ({
            label: type,
            value: count,
            color: getChecklistTypeColor(type)
          }))}
          total={statistics.totalRecords}
        />
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Distribution by Company */}
        <section className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-indigo-600" />
            <h3 className="text-xl font-bold text-gray-800">Distribuzione per Compagnia</h3>
          </div>
          <DonutChart
            data={Object.entries(statistics.byCompany)
              .sort(([, a]: [any, any], [, b]: [any, any]) => b - a)
              .map(([company, count]: [string, any], index) => ({
                label: company,
                value: count,
                color: companyColors[index % companyColors.length]
              }))}
            total={statistics.totalRecords}
          />
        </section>

        {/* Distribution by Bank */}
        <section className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Home className="w-6 h-6 text-indigo-600" />
            <h3 className="text-xl font-bold text-gray-800">Distribuzione per Banca</h3>
          </div>
          <DonutChart
            data={Object.entries(statistics.byBank)
              .sort(([, a]: [any, any], [, b]: [any, any]) => b - a)
              .map(([bank, count]: [string, any], index) => ({
                label: bank,
                value: count,
                color: bankColors[index % bankColors.length]
              }))}
            total={statistics.totalRecords}
          />
        </section>
      </div>
    </div>
  );
}
